/**
 * useVisualization
 *
 * Simplified composable for transaction visualization only.
 * Shows transactions traveling from spawn points to the sequencer.
 */

import { ref, onMounted, onUnmounted, watch } from 'vue';
import { Graphics } from 'pixi.js';
import { initPixiApp, type PixiAppInstance } from '@/core/PixiAppSetup';
import { TransactionRenderer } from '@/rendering/TransactionRenderer';
import { CircleRenderer } from '@/rendering/CircleRenderer';
import { BarGraphRenderer } from '@/rendering/BarGraphRenderer';
import { StatsRenderer } from '@/rendering/StatsRenderer';
import { BlockchainState } from '@/stores/blockchainState';

export interface VisualizationRenderers {
  transaction: TransactionRenderer | null;
  circle: CircleRenderer | null;
  barGraph: BarGraphRenderer | null;
  stats: StatsRenderer | null;
}

export function useVisualization(state: BlockchainState) {
  const pixiContainer = ref<HTMLElement>();
  let pixiInstance: PixiAppInstance | null = null;
  let onRenderersReady: (() => void) | null = null;

  const renderers: VisualizationRenderers = {
    transaction: null,
    circle: null,
    barGraph: null,
    stats: null
  };

  /**
   * Main animation update callback
   */
  const updateVisualization = (delta: number) => {
    if (!renderers.transaction || !renderers.circle || !renderers.barGraph || !renderers.stats) return;

    // Always update drag physics (runs even when paused for responsiveness)
    renderers.circle.updateDragPhysics();

    // Skip other updates if paused
    if (state.isPaused.value) {
      return;
    }

    // Update FPS
    state.updateFPS(performance.now());

    // Update stats display (pass delta for frame-independent animations)
    renderers.stats.update(delta);

    // Update circles (growth/decay animation and cleanup)
    renderers.circle.update(delta);

    // Update bar graph
    renderers.barGraph.update(delta);

    // Update transaction animations
    renderers.transaction.update(delta);
  };

  /**
   * Initialize Pixi and all renderers
   */
  const initializeVisualization = async () => {
    if (!pixiContainer.value) return;

    // Initialize Pixi app
    pixiInstance = await initPixiApp(pixiContainer.value, updateVisualization);

    // Create sprite pool
    pixiInstance.createSpritePool();

    // Create bar graph with sequencer
    renderers.barGraph = new BarGraphRenderer(
      pixiInstance.containers.barGraph,
      pixiInstance.containers.sequencer
    );
    // Initialize with saved chart mode
    renderers.barGraph.setChartMode(state.chartMode.value);

    // Create circle renderer (address-based visualization) with actual viewport dimensions
    renderers.circle = new CircleRenderer(
      pixiInstance.containers.circles,
      pixiInstance.worldSize.width,
      pixiInstance.worldSize.height
    );
    renderers.circle.start(); // Initialize (circles created on-demand based on transaction addresses)
    // Initialize with saved chart mode
    renderers.circle.setChartMode(state.chartMode.value);

    // Set viewport info for drag coordinate conversion
    renderers.circle.setViewportInfo(
      pixiInstance.viewport.scale.x,
      pixiInstance.app.canvas as HTMLCanvasElement
    );

    // Create stats renderer (logo and stats panel)
    renderers.stats = new StatsRenderer(pixiInstance.containers.stats, state);

    // Connect stats renderer to circle renderer for address-specific stats
    if (renderers.circle) {
      renderers.stats.setCircleMetricsGetter((address) =>
        renderers.circle?.getMetricsForAddress(address) || null
      );
      renderers.stats.setProjectNameGetter((address) =>
        renderers.circle?.getProjectNameForAddress(address) || null
      );
    }

    // Create transaction renderer
    renderers.transaction = new TransactionRenderer(
      pixiInstance.containers.tx,
      pixiInstance.sprites.txSprites
    );
    renderers.transaction.setCircleGetter(() =>
      renderers.circle?.getActiveCircles() || []
    );

    // Connect spawn indicator for bullet targeting
    if (renderers.barGraph) {
      renderers.transaction.setSpawnIndicatorGetters(
        (travelTime) => renderers.barGraph!.getSpawnIndicatorFutureX(travelTime),
        () => renderers.barGraph!.getSpawnIndicatorY()
      );

      // Connect transaction hit callback to accelerate sequencer spin
      renderers.transaction.setTransactionHitCallback((count) => {
        renderers.barGraph!.onTransactionHit(count);
      });
    }

    // Register resize callback to update CircleRenderer bounds
    pixiInstance.onResizeComplete((worldWidth, worldHeight) => {
      if (renderers.circle) {
        renderers.circle.updateBounds(worldWidth, worldHeight);
      }
    });

    // Set initial circle bounds using current world size
    if (renderers.circle) {
      renderers.circle.updateBounds(pixiInstance.worldSize.width, pixiInstance.worldSize.height);
    }

    // Notify that all renderers are ready
    if (onRenderersReady) {
      onRenderersReady();
    }
  };

  /**
   * Cleanup all renderers
   */
  const cleanupVisualization = () => {
    renderers.stats?.destroy();
    renderers.barGraph?.destroy();
    renderers.circle?.destroy();
    renderers.transaction?.destroy();
    pixiInstance?.cleanup();
  };

  /**
   * Clear all visual state (for pause/resume)
   */
  const clearVisualizationState = () => {
    renderers.transaction?.clear();
  };

  // Tutorial state
  let tutorialPulseInterval: number | null = null;
  let pulsePhase = 0;
  let tutorialGlowGraphics: Graphics | null = null;
  let currentHighlightedContainers: string[] = [];

  /**
   * Set tutorial highlight mode - dims non-highlighted elements, adds pulsing glow to highlighted ones
   * @param highlights Array of elements to keep bright: 'circles', 'sequencer', 'chart', 'stats', 'none'
   */
  const setTutorialHighlights = (highlights: string[]) => {
    if (!pixiInstance) return;

    const DIM_ALPHA = 0.15;
    const BRIGHT_ALPHA = 1.0;

    // Determine what should be bright
    const circlesBright = highlights.includes('circles');
    const sequencerBright = highlights.includes('sequencer');
    const chartBright = highlights.includes('chart');
    const statsBright = highlights.includes('stats');

    // If no highlights or 'none', reset all to normal and stop pulsing
    if (highlights.length === 0 || highlights.includes('none')) {
      if (tutorialPulseInterval) {
        clearInterval(tutorialPulseInterval);
        tutorialPulseInterval = null;
      }
      // Remove glow graphics
      if (tutorialGlowGraphics) {
        tutorialGlowGraphics.destroy();
        tutorialGlowGraphics = null;
      }
      currentHighlightedContainers = [];

      pixiInstance.containers.circles.alpha = BRIGHT_ALPHA;
      pixiInstance.containers.tx.alpha = BRIGHT_ALPHA;
      pixiInstance.containers.sequencer.alpha = BRIGHT_ALPHA;
      pixiInstance.containers.barGraph.alpha = BRIGHT_ALPHA;
      pixiInstance.containers.stats.alpha = BRIGHT_ALPHA;
      return;
    }

    currentHighlightedContainers = highlights;

    // Transactions only visible when sequencer is highlighted (showing the flow)
    const txBright = sequencerBright;

    // Set base alpha values (dimmed or bright)
    pixiInstance.containers.circles.alpha = circlesBright ? BRIGHT_ALPHA : DIM_ALPHA;
    pixiInstance.containers.tx.alpha = txBright ? BRIGHT_ALPHA : DIM_ALPHA;
    pixiInstance.containers.sequencer.alpha = sequencerBright ? BRIGHT_ALPHA : DIM_ALPHA;
    pixiInstance.containers.barGraph.alpha = chartBright ? BRIGHT_ALPHA : DIM_ALPHA;
    pixiInstance.containers.stats.alpha = statsBright ? BRIGHT_ALPHA : DIM_ALPHA;

    // Create glow overlay graphics if needed
    if (!tutorialGlowGraphics) {
      tutorialGlowGraphics = new Graphics();
      pixiInstance.viewport.addChild(tutorialGlowGraphics);
    }

    // Start pulse animation
    if (tutorialPulseInterval) {
      clearInterval(tutorialPulseInterval);
    }

    // Use performance.now() for precise timing synced with CSS
    const startTime = performance.now();

    tutorialPulseInterval = window.setInterval(() => {
      if (!pixiInstance || !tutorialGlowGraphics) return;

      // 2 second cycle synced with CSS animation
      const elapsed = performance.now() - startTime;
      const cycleProgress = (elapsed % 2000) / 2000; // 0 to 1 over 2 seconds
      // Pulse alpha: 0.3 to 1.0 using sine wave (matching CSS ease-in-out)
      const pulseAlpha = 0.3 + 0.7 * (0.5 + 0.5 * Math.sin(cycleProgress * Math.PI * 2 - Math.PI / 2));
      const glowColor = 0x10B981; // Green accent color
      const strokeWidth = 3;
      const circleStrokeWidth = 6; // Double thickness for circles

      // Clear previous
      tutorialGlowGraphics.clear();

      // Step 1: Circle outline (only when sequencer NOT highlighted)
      const isStep1 = currentHighlightedContainers.includes('circles') &&
                      !currentHighlightedContainers.includes('sequencer');

      if (isStep1 && renderers.circle) {
        const circles = renderers.circle.getActiveCircles();
        for (const circle of circles) {
          tutorialGlowGraphics.circle(circle.x, circle.y, circle.radius + circleStrokeWidth / 2);
          tutorialGlowGraphics.stroke({ color: glowColor, alpha: pulseAlpha, width: circleStrokeWidth });
        }
      }

      // Step 2: Sequencer outline
      if (currentHighlightedContainers.includes('sequencer') && renderers.barGraph) {
        const seqX = renderers.barGraph.getSpawnIndicatorFutureX(0);
        const seqY = renderers.barGraph.getSpawnIndicatorY();
        tutorialGlowGraphics.circle(seqX, seqY, 45);
        tutorialGlowGraphics.stroke({ color: glowColor, alpha: pulseAlpha, width: circleStrokeWidth });
      }

      // Step 3: Stats boxes outline
      if (currentHighlightedContainers.includes('stats') && renderers.stats) {
        const statBounds = renderers.stats.getStatBlockBounds();
        for (const bounds of statBounds) {
          tutorialGlowGraphics.roundRect(bounds.x - strokeWidth / 2, bounds.y - strokeWidth / 2,
            bounds.width + strokeWidth, bounds.height + strokeWidth, 8);
          tutorialGlowGraphics.stroke({ color: glowColor, alpha: pulseAlpha, width: strokeWidth });
        }
      }
    }, 16); // 60fps for smooth animation
  };

  /**
   * Reset tutorial highlights (restore all to normal)
   */
  const resetTutorialHighlights = () => {
    setTutorialHighlights([]);
  };

  onMounted(async () => {
    await initializeVisualization();
  });

  onUnmounted(() => {
    cleanupVisualization();
  });

  // Watch for chart mode changes and update circle renderer
  watch(() => state.chartMode.value, (newMode) => {
    if (renderers.circle) {
      renderers.circle.setChartMode(newMode);
      // console.log(`🔵 Circle growth mode changed to: ${newMode}`);
    }
  });

  return {
    pixiContainer,
    pixiInstance: () => pixiInstance,
    renderers,
    clearVisualizationState,
    setTutorialHighlights,
    resetTutorialHighlights,
    setOnRenderersReady: (callback: () => void) => {
      onRenderersReady = callback;
    }
  };
}
